import React, { useState } from 'react';
import { useFacilityAvailability } from '../../hooks/useFacilityAvailability';
import { FACILITIES } from '../../hooks/facilityConfig';
import './CleaningSchedule.css';

const CleaningSchedule = () => {
  const [viewingTomorrow, setViewingTomorrow] = useState(false);
  
  // Facilities that need cleaning
  const cleaningFacilities = ['party-room', 'executive-dining', 'golf-lounge'];
  
  const { data, loading, error } = useFacilityAvailability({
    facilityIds: cleaningFacilities,
    days: 7, // Fetch a week to find next bookings
    autoRefetch: true,
    refetchInterval: 5 * 60 * 1000 // 5 minutes
  });

  const getDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr.replace(/\//g, '-'));
    return `${date.getMonth() + 1}月${date.getDate()}日`;
  };

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const targetDate = viewingTomorrow ? tomorrow : today;
  const targetDateStr = getDateString(targetDate);
  const dateLabel = viewingTomorrow ? '明日' : '本日';

  // Find cleaning periods for a facility on a specific date
  const findCleaningPeriods = (facilityId) => {
    if (!data || !data.facilities || !data.facilities[facilityId]) return [];
    
    const facilityData = data.facilities[facilityId];
    
    if (facilityData.error || !facilityData.days || facilityData.days.length === 0) return [];
    
    // Use index-based approach like TodayAvailabilityGrid
    const dayIndex = viewingTomorrow ? 1 : 0;
    const dayData = facilityData.days[dayIndex];
    if (!dayData || !dayData.slots) return [];
    
    const cleaningPeriods = [];
    // After processing, booked slots have status 'booked' and available: false
    const bookedSlots = dayData.slots.filter(slot => slot.status === 'booked' || !slot.available);
    
    // Sort booked slots by time
    bookedSlots.sort((a, b) => a.time.localeCompare(b.time));
    
    // Find gaps between bookings that need cleaning
    for (let i = 0; i < bookedSlots.length - 1; i++) {
      const currentBooking = bookedSlots[i];
      const nextBooking = bookedSlots[i + 1];
      
      // Calculate end time of current booking (add 30 min to last slot)
      const [currHour, currMin] = currentBooking.time.split(':').map(Number);
      let endHour = currHour;
      let endMin = currMin + 30;
      if (endMin >= 60) {
        endMin -= 60;
        endHour++;
      }
      const currentEnd = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
      
      // Check if there's a gap between current end and next start
      if (currentEnd < nextBooking.time) {
        // Find the actual booking ranges
        let bookingStart = currentBooking.time;
        let bookingEnd = currentEnd;
        
        // Find continuous booking blocks
        let j = i;
        while (j > 0 && bookedSlots[j].time === bookedSlots[j-1].time.replace(/(\d{2}):(\d{2})/, (m, h, min) => {
          let nextMin = parseInt(min) + 30;
          let nextHour = parseInt(h);
          if (nextMin >= 60) {
            nextMin -= 60;
            nextHour++;
          }
          return `${nextHour.toString().padStart(2, '0')}:${nextMin.toString().padStart(2, '0')}`;
        })) {
          j--;
        }
        bookingStart = bookedSlots[j].time;
        
        // Find end of current booking block
        j = i;
        while (j < bookedSlots.length - 1 && bookedSlots[j+1].time === bookingEnd) {
          const [h, m] = bookedSlots[j+1].time.split(':').map(Number);
          let newEndHour = h;
          let newEndMin = m + 30;
          if (newEndMin >= 60) {
            newEndMin -= 60;
            newEndHour++;
          }
          bookingEnd = `${newEndHour.toString().padStart(2, '0')}:${newEndMin.toString().padStart(2, '0')}`;
          j++;
          i = j; // Skip processed slots
        }
        
        // Find start of next booking block
        let nextStart = nextBooking.time;
        j = i + 1;
        while (j < bookedSlots.length - 1 && bookedSlots[j].time.replace(/(\d{2}):(\d{2})/, (m, h, min) => {
          let prevMin = parseInt(min) - 30;
          let prevHour = parseInt(h);
          if (prevMin < 0) {
            prevMin += 60;
            prevHour--;
          }
          return `${prevHour.toString().padStart(2, '0')}:${prevMin.toString().padStart(2, '0')}`;
        }) === bookedSlots[j-1].time) {
          nextStart = bookedSlots[j].time;
          j--;
        }
        
        cleaningPeriods.push({
          cleaningStart: currentEnd,
          cleaningEnd: nextBooking.time,
          previousBooking: { start: bookingStart, end: bookingEnd },
          nextBooking: { start: nextStart }
        });
      }
    }
    
    // Check if cleaning is needed after the last booking
    if (bookedSlots.length > 0) {
      const lastBooking = bookedSlots[bookedSlots.length - 1];
      const [lastHour, lastMin] = lastBooking.time.split(':').map(Number);
      let endHour = lastHour;
      let endMin = lastMin + 30;
      if (endMin >= 60) {
        endMin -= 60;
        endHour++;
      }
      const lastEnd = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
      
      // Check if there's time left in the day after the last booking
      // Party Room and Executive Dining close at 22:00, Golf Lounge at 23:00
      const closingTime = facilityId === 'golf-lounge' ? '23:00' : '22:00';
      
      if (lastEnd < closingTime) {
        // Find the actual end of the last booking block
        let bookingStart = lastBooking.time;
        
        // Find start of last booking block
        let j = bookedSlots.length - 1;
        while (j > 0) {
          const prevSlot = bookedSlots[j - 1];
          const [prevHour, prevMin] = prevSlot.time.split(':').map(Number);
          let nextHour = prevHour;
          let nextMin = prevMin + 30;
          if (nextMin >= 60) {
            nextMin -= 60;
            nextHour++;
          }
          const nextTime = `${nextHour.toString().padStart(2, '0')}:${nextMin.toString().padStart(2, '0')}`;
          
          if (nextTime === bookedSlots[j].time) {
            bookingStart = prevSlot.time;
            j--;
          } else {
            break;
          }
        }
        
        cleaningPeriods.push({
          cleaningStart: lastEnd,
          cleaningEnd: null, // No end time when there's no next booking
          previousBooking: { start: bookingStart, end: lastEnd },
          nextBooking: null // No next booking for the day
        });
      }
    }
    
    return cleaningPeriods;
  };

  // Find next booking for a facility
  const findNextBooking = (facilityId) => {
    if (!data || !data.facilities || !data.facilities[facilityId]) return null;
    
    const facilityData = data.facilities[facilityId];
    if (facilityData.error || !facilityData.days) return null;
    
    // Start from tomorrow if viewing today, or day after tomorrow if viewing tomorrow
    const startIndex = viewingTomorrow ? 2 : 1;
    
    for (let i = startIndex; i < facilityData.days.length; i++) {
      const day = facilityData.days[i];
      const bookedSlots = day.slots?.filter(slot => slot.status === 'booked' || !slot.available) || [];
      if (bookedSlots.length > 0) {
        const firstSlot = bookedSlots.sort((a, b) => a.time.localeCompare(b.time))[0];
        return { date: day.date, time: firstSlot.time };
      }
    }
    
    return null;
  };

  // Get facility display name
  const getFacilityName = (facilityId) => {
    const names = {
      'party-room': 'パーティルーム',
      'executive-dining': 'エグゼクティブダイニングルーム',
      'golf-lounge': 'ゴルフラウンジ'
    };
    return names[facilityId] || facilityId;
  };

  // Helper to get facility by ID
  const getFacilityById = (facilityId) => {
    return Object.values(FACILITIES).find(f => f.id === facilityId);
  };

  // Generate time slots from facility open to close time in 30-minute increments
  const generateTimeSlotsForFacility = (facilityId) => {
    const facility = getFacilityById(facilityId);
    if (!facility) return [];
    
    const [openHour] = facility.openTime.split(':').map(Number);
    const [closeHour] = facility.closeTime.split(':').map(Number);
    
    const slots = [];
    for (let hour = openHour; hour < closeHour; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    // Add final hour
    if (closeHour <= 23) {
      slots.push(`${closeHour.toString().padStart(2, '0')}:00`);
    }
    
    return slots;
  };

  // Get slot status for a specific facility and time
  const getSlotStatus = (facilityId, time) => {
    if (!data || !data.facilities || !data.facilities[facilityId]) return 'unknown';
    
    const facilityData = data.facilities[facilityId];
    if (facilityData.error || !facilityData.days || facilityData.days.length === 0) return 'unknown';
    
    // Find target date's data
    const dayIndex = viewingTomorrow ? 1 : 0;
    const targetData = facilityData.days[dayIndex];
    if (!targetData || !targetData.slots) return 'unknown';

    // Find the slot status
    const slot = targetData.slots.find(s => s.time === time);
    if (!slot) return 'unknown';

    // Check if slot is available
    if (slot.status === 'booked' || !slot.available) return 'booked';
    if (slot.status === 'past-deadline') return 'past-deadline';
    
    return 'available';
  };

  // Get cell content based on status
  const getCellContent = (status) => {
    switch (status) {
      case 'available':
        return '◯';
      case 'booked':
        return '✕';
      case 'past-deadline':
        return '/';
      default:
        return '?';
    }
  };

  // Get cell class based on status
  const getCellClass = (status) => {
    switch (status) {
      case 'available':
        return 'slot-available';
      case 'booked':
        return 'slot-booked';
      case 'past-deadline':
        return 'slot-past-deadline';
      default:
        return 'slot-unknown';
    }
  };

  // Calculate days difference
  const daysDifference = (date1Str, date2Str) => {
    const date1 = new Date(date1Str.replace(/\//g, '-'));
    const date2 = new Date(date2Str.replace(/\//g, '-'));
    const diffTime = Math.abs(date2 - date1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) return <div className="cleaning-schedule-loading">読み込み中...</div>;
  if (error) return <div className="cleaning-schedule-error">エラー: {error}</div>;

  return (
    <div className="cleaning-schedule">
      <div className="cleaning-header">
        <h2>清掃スケジュール - {dateLabel} ({formatDateDisplay(targetDateStr)})</h2>
        <div className="date-toggle">
          <button 
            className={!viewingTomorrow ? 'active' : ''} 
            onClick={() => setViewingTomorrow(false)}
          >
            本日
          </button>
          <button 
            className={viewingTomorrow ? 'active' : ''} 
            onClick={() => setViewingTomorrow(true)}
          >
            明日
          </button>
        </div>
      </div>

      <div className="cleaning-list">
        {cleaningFacilities.map(facilityId => {
          const cleaningPeriods = findCleaningPeriods(facilityId);
          const dayIndex = viewingTomorrow ? 1 : 0;
          const dayData = data?.facilities?.[facilityId]?.days?.[dayIndex];
          const bookedSlotsCount = dayData?.slots?.filter(s => s.status === 'booked' || !s.available).length || 0;
          const hasBookingsToday = bookedSlotsCount > 0;
          
          return (
            <div key={facilityId} className="facility-section">
              <h3>{getFacilityName(facilityId)}</h3>
              
              {/* Compact 30-minute slot availability display */}
              <div className="facility-slots-compact">
                <div className="slots-timeline">
                  <div className="time-labels">
                    <span className="time-start">{getFacilityById(facilityId)?.openTime || '10:00'}</span>
                    <span className="time-end">{getFacilityById(facilityId)?.closeTime || '22:00'}</span>
                  </div>
                  <div className="slots-bar-container">
                    <div className="slots-bar">
                      {generateTimeSlotsForFacility(facilityId).map((time, index, allSlots) => {
                        const status = getSlotStatus(facilityId, time);
                        const nextStatus = index < allSlots.length - 1 ? getSlotStatus(facilityId, allSlots[index + 1]) : null;
                        const isTransition = nextStatus && status !== nextStatus;
                        
                        return (
                          <div
                            key={`${facilityId}-${time}`}
                            className={`slot-segment ${getCellClass(status)}`}
                            title={`${time} - ${status === 'available' ? '利用可能' : status === 'booked' ? '予約済み' : '予約締切'}`}
                          >
                            {isTransition && (
                              <div className="transition-time">
                                {allSlots[index + 1]}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              
              {cleaningPeriods.length === 0 ? (
                <div className="no-cleaning">
                  <div className="cleaning-item no-cleaning-needed">
                    □ {dateLabel}は清掃の必要はありません
                  </div>
                  <ul className="booking-details">
                    {!hasBookingsToday ? (
                      <li>• {dateLabel}の予約はありません</li>
                    ) : (
                      <li>• 予約がありますが、清掃時間は必要ありません</li>
                    )}
                    {(() => {
                      const nextBooking = findNextBooking(facilityId);
                      if (nextBooking) {
                        const daysDiff = daysDifference(targetDateStr, nextBooking.date);
                        if (daysDiff === 1) {
                          return <li>• 次の予約: 明日 {nextBooking.time}</li>;
                        } else if (daysDiff > 2) {
                          return <li>• 次の予約: {formatDateDisplay(nextBooking.date)} {nextBooking.time} ({daysDiff}日後)</li>;
                        } else {
                          return <li>• 次の予約: {formatDateDisplay(nextBooking.date)} {nextBooking.time}</li>;
                        }
                      } else {
                        return <li>• 今後7日間予約はありません</li>;
                      }
                    })()}
                  </ul>
                </div>
              ) : (
                cleaningPeriods.map((period, index) => (
                  <div key={index} className="cleaning-item">
                    <div className="cleaning-time">
                      □ {period.cleaningStart}{period.cleaningEnd ? `-${period.cleaningEnd}` : ' 以降'} - 清掃が必要です
                    </div>
                    <ul className="booking-details">
                      <li>• 前の予約: {period.previousBooking.start}-{period.previousBooking.end}</li>
                      {period.nextBooking ? (
                        <li>• 次の予約: {period.nextBooking.start}-{(() => {
                          // Find the end time of next booking
                          const dayIndex = viewingTomorrow ? 1 : 0;
                          const dayData = data.facilities[facilityId].days[dayIndex];
                          let endTime = period.nextBooking.start;
                          if (dayData && dayData.slots) {
                            let foundStart = false;
                            for (const slot of dayData.slots) {
                              if (slot.time === period.nextBooking.start && (slot.status === 'booked' || !slot.available)) {
                                foundStart = true;
                              } else if (foundStart && slot.status !== 'booked' && slot.available) {
                                break;
                              } else if (foundStart) {
                                const [sh, sm] = slot.time.split(':').map(Number);
                                let eh = sh;
                                let em = sm + 30;
                                if (em >= 60) {
                                  em -= 60;
                                  eh++;
                                }
                                endTime = `${eh.toString().padStart(2, '0')}:${em.toString().padStart(2, '0')}`;
                              }
                            }
                          }
                          return endTime;
                        })()}</li>
                      ) : (
                        <li>• 本日はこれ以上の予約はありません</li>
                      )}
                    </ul>
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CleaningSchedule;